import { useState } from 'react';
import StaffPageShell from '../components/StaffPageShell';
import { ThemeButton } from '../components/ThemeWrapper';
import usePermissions from '../hooks/usePermissions';
import useServerCapacity from '../hooks/useServerCapacity';

function show(value, suffix = '') {
  if (value == null || value === '') return 'Not available';
  return `${value}${suffix}`;
}

function CapacityChart({ series }) {
  if (!series || series.length < 2) {
    return (
      <p className="text-sm theme-text-secondary">
        Insufficient historical data. A trend needs at least two stored discovery samples. Missing days are collection
        gaps, not zero load.
      </p>
    );
  }
  const width = 680;
  const height = 200;
  const pad = 28;
  const max = Math.max(100, ...series.map((point) => Math.max(point.cpuPct || 0, point.ramPct || 0)));
  const x = (index) => pad + (index * (width - pad * 2)) / (series.length - 1);
  const y = (value) => height - pad - ((Number(value) || 0) / max) * (height - pad * 2);
  const line = (key) => series.map((point, index) => `${x(index)},${y(point[key])}`).join(' ');
  return (
    <div>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="CPU and RAM samples in the selected window" className="w-full">
        <line x1={pad} y1={height - pad} x2={width - pad} y2={height - pad} stroke="#cbd5e1" />
        <polyline fill="none" stroke="#1d4ed8" strokeWidth="2" points={line('cpuPct')} />
        <polyline fill="none" stroke="#0f766e" strokeWidth="2" points={line('ramPct')} />
      </svg>
      <p className="text-xs theme-text-secondary">
        Blue is CPU percent. Teal is RAM percent. Points are stored discovery runs from {series[0].label} to{' '}
        {series[series.length - 1].label}.
      </p>
    </div>
  );
}

function ServerCapacityPage() {
  const { role } = usePermissions();
  const capacity = useServerCapacity();
  const [selected, setSelected] = useState('');
  const report = capacity.report;
  const hosts = report?.hosts || [];
  const plan = report?.plan;
  const selectedHost = hosts.find((host) => host.alias === selected) || hosts[0] || null;

  if (role !== 'superadmin') {
    return (
      <StaffPageShell title="Server capacity" subtitle="Super Admin only">
        <p className="text-sm theme-text-secondary">This page is for Super Admin users.</p>
      </StaffPageShell>
    );
  }

  return (
    <StaffPageShell
      wide
      title="Server capacity and user allocation"
      subtitle="Read-only planning for where additional lab accounts could fit. Nothing on this page changes a server."
    >
      <style>{`@media print { .no-print { display: none !important; } }`}</style>
      <div className="profile-card theme-animate p-6 space-y-6" id="capacity-report">
        <p className="text-sm theme-text-secondary">
          Figures come from lab discovery snapshots and server-account scans already stored in SMS. This page does not
          install collectors, restart services, create accounts, change quotas, or migrate data. Registered homes, VNC
          services, logged-in users, and running workloads are counted separately. Where a measurement was not
          collected, the cell says Not available. Where the selected window has too few samples, it says Insufficient
          historical data.
        </p>

        <div className="no-print grid gap-3 md:grid-cols-4">
          <label className="text-sm">
            Period
            <select className="mt-1 w-full" value={capacity.days} onChange={(event) => capacity.setDays(Number(event.target.value))}>
              {[7, 15, 30, 60, 90].map((days) => (
                <option key={days} value={days}>
                  Last {days} days
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            Server
            <select className="mt-1 w-full" value={capacity.server} onChange={(event) => capacity.setServer(event.target.value)}>
              <option value="">All servers</option>
              {(report?.filters?.servers || []).map((alias) => (
                <option key={alias} value={alias}>
                  {alias}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            Student batch
            <select className="mt-1 w-full" value={capacity.batch} onChange={(event) => capacity.setBatch(event.target.value)}>
              <option value="">All batches</option>
              {(report?.filters?.batches || []).map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <div className="text-sm">
            <div>Physical host</div>
            <p className="mt-1 theme-text-secondary">{report?.filters?.physicalHost?.reason || 'Not available.'}</p>
          </div>
        </div>
        <p className="text-sm theme-text-secondary">
          Location filter: {report?.filters?.location?.reason || 'Not available.'} {report?.filters?.batchSupport}
        </p>
        {report?.period ? (
          <p className="text-sm">
            Selected window: {report.period.startLabel} to {report.period.endLabel} ({report.period.timezoneLabel}).{' '}
            {report.period.message}
          </p>
        ) : null}
        {capacity.loading ? <p className="text-sm">Updating the recommendation…</p> : null}
        {capacity.error ? <p className="text-sm text-red-700">{capacity.error}</p> : null}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left">
                <th>Server</th>
                <th>Status</th>
                <th>Last success</th>
                <th>Host</th>
                <th>vCPU</th>
                <th>CPU avg / p95 / peak</th>
                <th>Load / CPU peak</th>
                <th>RAM peak</th>
                <th>Disk free</th>
                <th>Homes</th>
                <th>VNC now / peak</th>
              </tr>
            </thead>
            <tbody>
              {hosts.map((host) => (
                <tr key={host.alias} className={selectedHost?.alias === host.alias ? 'bg-slate-50' : undefined}>
                  <td>
                    <button type="button" className="underline" onClick={() => setSelected(host.alias)}>
                      {host.alias}
                    </button>
                  </td>
                  <td>{host.stale ? `${host.status}, stale` : host.status}</td>
                  <td>{host.lastSuccessLabel || 'Not available'}</td>
                  <td title={host.relationship?.detail}>{host.relationship?.physicalHost || host.relationship?.kind || 'Not available'}</td>
                  <td>{show(host.metrics?.cpuCount)}</td>
                  <td title={host.unavailable?.cpuP95}>
                    {show(host.metrics?.cpuPctAvg, '%')} / {show(host.metrics?.cpuPctP95, '%')} / {show(host.metrics?.cpuPctPeak, '%')}
                  </td>
                  <td>{show(host.metrics?.loadNormPeak)}</td>
                  <td>{show(host.metrics?.ramPctPeak, '%')}</td>
                  <td title={host.unavailable?.disk}>{show(host.metrics?.diskAvailGb, ' GB')}</td>
                  <td title={host.accounts?.note}>{show(host.accounts?.homes)}</td>
                  <td>
                    {show(host.accounts?.vncLatest)} / {show(host.accounts?.vncPeak)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {selectedHost ? (
          <section className="space-y-3">
            <h2 className="text-lg font-semibold">{selectedHost.alias} detail</h2>
            <p className="text-sm">{selectedHost.coverage?.message}</p>
            <CapacityChart series={selectedHost.series} />
            {selectedHost.busiest ? (
              <p className="text-sm">
                Busiest stored sample: {selectedHost.busiest.label}. CPU {show(selectedHost.busiest.cpuPct, '%')}, RAM{' '}
                {show(selectedHost.busiest.ramPct, '%')}.
              </p>
            ) : (
              <p className="text-sm">Insufficient historical data to describe a busy period.</p>
            )}
            <dl className="grid gap-2 text-sm md:grid-cols-2">
              <div>Physical host / VM: {selectedHost.relationship?.detail}</div>
              <div title={selectedHost.unavailable?.cpuSample}>
                CPU average {show(selectedHost.metrics?.cpuPctAvg, '%')}, 95th {show(selectedHost.metrics?.cpuPctP95, '%')}, peak{' '}
                {show(selectedHost.metrics?.cpuPctPeak, '%')}. {selectedHost.unavailable?.cpuSample}
              </div>
              <div>
                Load average / CPU count: average {show(selectedHost.metrics?.loadNormAvg)}, peak {show(selectedHost.metrics?.loadNormPeak)}.
              </div>
              <div>
                RAM capacity {show(selectedHost.metrics?.ramGb, ' GB')}, average used {show(selectedHost.metrics?.ramUsedAvgGb, ' GB')}, peak used{' '}
                {show(selectedHost.metrics?.ramUsedPeakGb, ' GB')}, minimum available {show(selectedHost.metrics?.ramAvailMinGb, ' GB')}.
              </div>
              <div title={selectedHost.unavailable?.swap}>Swap: {selectedHost.unavailable?.swap}</div>
              <div title={selectedHost.unavailable?.disk}>
                Disk capacity {show(selectedHost.metrics?.diskTotalGb, ' GB')}, free {show(selectedHost.metrics?.diskAvailGb, ' GB')}, growth{' '}
                {show(selectedHost.metrics?.diskGrowthGb, ' GB')}. {selectedHost.unavailable?.disk}
              </div>
              <div title={selectedHost.unavailable?.io}>Disk I/O: {selectedHost.unavailable?.io}</div>
              <div title={selectedHost.accounts?.note}>
                Registered homes {show(selectedHost.accounts?.homes)}. SMS-created accounts {show(selectedHost.accounts?.smsAccounts)}. VNC
                services now {show(selectedHost.accounts?.vncLatest)}, peak in window {show(selectedHost.accounts?.vncPeak)}. Logged-in users:{' '}
                {selectedHost.unavailable?.loggedIn}
              </div>
              <div>Running workloads: {selectedHost.unavailable?.workloads}</div>
              <div>Required tools: {selectedHost.unavailable?.tools}</div>
              <div>Licenses: {selectedHost.unavailable?.licenses}</div>
              <div>PDK / lab mounts: {selectedHost.unavailable?.mounts}</div>
              <div>
                Existing quota commitment {show(selectedHost.accounts?.quotaHardGb, ' GB')}, quota used{' '}
                {show(selectedHost.accounts?.quotaUsedGb, ' GB')}.
              </div>
              <div>{selectedHost.unavailable?.maintenance}</div>
            </dl>
          </section>
        ) : null}

        <section className="space-y-3 no-print">
          <h2 className="text-lg font-semibold">Allocation planner</h2>
          <p className="text-sm theme-text-secondary">{plan?.estimateNote}</p>
          <p className="text-sm theme-text-secondary">
            {capacity.settingsMeta?.saved
              ? `These assumptions are stored in SMS${capacity.settingsMeta.updatedByEmail ? ` by ${capacity.settingsMeta.updatedByEmail}` : ''}${capacity.settingsMeta.updatedLabel ? ` on ${capacity.settingsMeta.updatedLabel}` : ''}.`
              : 'These assumptions are not stored yet. Save them to keep this planner setup for the organization.'}
          </p>
          <div className="grid gap-3 md:grid-cols-3">
            <NumberField label="Users to place" value={capacity.assumptions.users} onChange={(value) => capacity.updateAssumption('users', value)} />
            <NumberField label="Concurrency percent" value={capacity.assumptions.concurrencyPct} onChange={(value) => capacity.updateAssumption('concurrencyPct', value)} />
            <NumberField label="CPU per active user" value={capacity.assumptions.cpuPerActiveUser} step="0.1" onChange={(value) => capacity.updateAssumption('cpuPerActiveUser', value)} />
            <NumberField label="RAM GB per active user" value={capacity.assumptions.ramGbPerActiveUser} step="0.5" onChange={(value) => capacity.updateAssumption('ramGbPerActiveUser', value)} />
            <NumberField label="Storage GB per account" value={capacity.assumptions.storageGbPerAccount} onChange={(value) => capacity.updateAssumption('storageGbPerAccount', value)} />
            <NumberField label="Safety headroom percent" value={capacity.assumptions.headroomPct} onChange={(value) => capacity.updateAssumption('headroomPct', value)} />
            <label className="text-sm md:col-span-2">
              Workload type
              <input className="mt-1 w-full" value={capacity.assumptions.workloadType} onChange={(event) => capacity.updateAssumption('workloadType', event.target.value)} />
            </label>
            <label className="text-sm">
              Required tools, comma separated
              <input className="mt-1 w-full" value={capacity.assumptions.tools} onChange={(event) => capacity.updateAssumption('tools', event.target.value)} />
            </label>
            <label className="text-sm flex items-center gap-2">
              <input type="checkbox" checked={capacity.assumptions.migrateData} onChange={(event) => capacity.updateAssumption('migrateData', event.target.checked)} />
              Existing student data must be migrated
            </label>
          </div>
          <ThemeButton type="button" disabled={capacity.saving} onClick={() => capacity.saveSettings()}>
            Save assumptions
          </ThemeButton>
          <div className="grid gap-2 md:grid-cols-2">
            {hosts.map((host) => (
              <div key={host.alias} className="text-sm flex gap-4">
                <span className="w-16">{host.alias}</span>
                <label>
                  <input type="checkbox" checked={(capacity.assumptions.excludedAliases || []).includes(host.alias)} onChange={() => capacity.toggleAlias('excludedAliases', host.alias)} /> Exclude
                </label>
                <label>
                  <input type="checkbox" checked={(capacity.assumptions.maintenanceAliases || []).includes(host.alias)} onChange={() => capacity.toggleAlias('maintenanceAliases', host.alias)} /> Mark maintenance
                </label>
              </div>
            ))}
          </div>
        </section>

        {plan ? (
          <section className="space-y-3">
            <h2 className="text-lg font-semibold">Recommendation</h2>
            <p className="text-sm">{plan.shortfallMessage}</p>
            <p className="text-sm theme-text-secondary">{plan.sharedHostNote}</p>
            <p className="text-sm">
              Safe allocated accounts: {plan.allocated}. Assigned in this plan: {plan.assignedTotal}. Unallocated remainder:{' '}
              {plan.unallocated} of {plan.requested}.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left">
                    <th>Server</th>
                    <th>Accounts</th>
                    <th>Concurrent</th>
                    <th>Projected CPU</th>
                    <th>Projected RAM</th>
                    <th>Disk left GB</th>
                    <th>Headroom accounts</th>
                    <th>Limiter</th>
                    <th>Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {(plan.rows || []).map((row) => (
                    <tr key={row.alias}>
                      <td>
                        {row.alias}
                        <div className="text-xs theme-text-secondary">{row.status}</div>
                      </td>
                      <td>
                        <input
                          className="w-20"
                          type="number"
                          min="0"
                          aria-label={`Accounts on ${row.alias}`}
                          value={capacity.overrides[row.alias] ?? ''}
                          placeholder={String(row.assignedAccounts)}
                          onChange={(event) => capacity.setOverride(row.alias, event.target.value)}
                        />
                        <div className="text-xs theme-text-secondary">
                          Safe {row.safeAccounts}
                          {row.overAccounts > 0 ? `, ${row.overAccounts} over budget` : ''}
                        </div>
                      </td>
                      <td>{row.expectedConcurrent}</td>
                      <td>{show(row.demand?.projectedCpuPct, '%')}</td>
                      <td>{show(row.demand?.projectedRamPct, '%')}</td>
                      <td>{show(row.demand?.projectedDiskRemainingGb)}</td>
                      <td>{row.headroomAccounts}</td>
                      <td>{row.limiter}</td>
                      <td>{row.confidence}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ul className="text-sm space-y-2">
              {(plan.rows || []).map((row) => (
                <li key={`${row.alias}-why`}>
                  <strong>{row.alias}.</strong> {row.reasons.join(' ')}
                </li>
              ))}
            </ul>
            <p className="text-sm">{plan.promise}</p>
            <div className="no-print flex flex-wrap gap-2">
              <ThemeButton type="button" disabled={capacity.saving} onClick={() => capacity.save()}>
                Save draft in SMS
              </ThemeButton>
              <ThemeButton type="button" variant="secondary" onClick={() => window.print()}>
                Print report
              </ThemeButton>
            </div>
          </section>
        ) : null}

        <section className="space-y-2 no-print">
          <h2 className="text-lg font-semibold">Saved plans</h2>
          <p className="text-sm theme-text-secondary">
            Approval records who accepted the plan. It does not create accounts or start a migration. Apply any change
            from the existing lab-user pages after you have reviewed it.
          </p>
          {(capacity.plans || []).map((item) => (
            <div key={item.id} className="text-sm border-t py-2">
              <div>
                {item.status} · {item.requestedUsers} users · {item.periodDays} days · created by {item.createdByEmail || 'unknown'}
                {item.approvedByEmail ? ` · approved by ${item.approvedByEmail}` : ''}
              </div>
              <div className="theme-text-secondary">{item.outcome}: {item.outcomeNote}</div>
              <div className="flex gap-2 mt-1">
                {item.status === 'draft' ? (
                  <ThemeButton type="button" variant="secondary" disabled={capacity.saving} onClick={() => capacity.approve(item.id)}>
                    Approve record
                  </ThemeButton>
                ) : null}
                <ThemeButton type="button" variant="secondary" onClick={() => capacity.download(item.id)}>
                  Download CSV
                </ThemeButton>
              </div>
            </div>
          ))}
          {!capacity.plans?.length ? <p className="text-sm theme-text-secondary">No saved plans yet.</p> : null}
        </section>
      </div>
    </StaffPageShell>
  );
}

function NumberField({ label, value, onChange, step = '1' }) {
  return (
    <label className="text-sm">
      {label}
      <input className="mt-1 w-full" type="number" step={step} value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

export default ServerCapacityPage;
